import { pdfOptions } from 'ComponentLibrary/src/common/pdf'
import {
    ArrowLeftIcon,
    ArrowRightIcon,
    CaretLeftIcon,
    CaretRightIcon,
    DownloadSimpleIcon,
    HighlighterIcon,
    MagnifyingGlassIcon,
    MinusIcon,
    PlusCircleIcon,
    PlusIcon,
    XIcon,
} from '@phosphor-icons/react'
import { Box, CircularProgress, ClickAwayListener, InputBase, Stack } from '@mui/material'
import { AssembleDocumentLottie, LottieWrapper, tokens } from 'ComponentLibrary'
import { IconButton, Typography } from 'ComponentLibrary/src/piazza'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import { RefObject, useEffect, useRef, useState } from 'react'
import { Document, Page } from 'react-pdf'
import type { CustomerExistingValuation as ExistingValuation } from '@einwert/valuation-api-client'
import { range } from 'lodash'
import { TextBubble, TextBubbleCreation, type TextBubbleCopy } from 'ComponentLibrary/src/widgets/Annotations'
import { usePdfSearch } from './usePdfSearch'
import { useDownloadReport } from 'ClientPortal/src/api/Queries/valuations'
import { PdfAnnotation, Scaled, TextSelection } from '@einwert/valuation-api-client'
import { useValuationPlausibility } from './ValuationPlausibility.data'
import { useTranslation } from 'react-i18next'
import type { UserLinkCopy } from 'ComponentLibrary/src/piazza/text/UserLink/UserLink'
import { useCurrentResolvedLocale } from 'ClientPortal/src/i18n/currentLocale'

type PlausibilityPdfTypes = {
    valuation?: ExistingValuation
    PdfAnnotations: PdfAnnotation[]
    isImmediatelyVisible?: boolean
    hasMultipleDrafts: boolean
    goToPage: (page: number) => void
    activePage: number
    setActivePage: (page: number) => void
    jumpPageInput: string
    setJumpPageInput: (input: string) => void
    numberOfPages: number
    setNumberOfPages: (numberOfPages: number) => void
    pageRefs: RefObject<(HTMLDivElement | null)[]>
    hasNewerDraft: boolean
    textBubbleCopy: TextBubbleCopy
    userLinkCopy: Partial<UserLinkCopy>
}

export default function PlausibilityPdf({
    valuation,
    PdfAnnotations,
    isImmediatelyVisible,
    hasMultipleDrafts,
    goToPage,
    activePage,
    setActivePage,
    setJumpPageInput,
    jumpPageInput,
    numberOfPages,
    setNumberOfPages,
    pageRefs,
    hasNewerDraft,
    textBubbleCopy,
    userLinkCopy,
}: PlausibilityPdfTypes) {
    const { t: tPlausibilityPdf } = useTranslation('pages', {
        keyPrefix: 'assets.assetDetails.valuation.valuationPlausibility.plausibilityPdf',
    })
    const locale = useCurrentResolvedLocale()
    const {
        addAnnotation,
        addReply,
        editAnnotation,
        editReply,
        deleteAnnotation,
        deleteReply,
        currentUser,
        isAddAnnotationPending,
        isEditAnnotationPending,
        isDeleteAnnotationPending,
        isValuationPlausibilityPending,
    } = useValuationPlausibility()
    const [hoveredAnnotationId, setHoveredAnnotationId] = useState<string | undefined>()
    const [downloadedPdf, setDownloadedPdf] = useState<Blob | null>(null)
    const [scale, setScale] = useState(1.0)
    const [annotationPanelWidth, setAnnotationPanelWidth] = useState<number>(0)
    const [selectedAnnotationId, setSelectedAnnotationId] = useState<string | undefined>()

    const { downloadReport, isDownloadingReport } = useDownloadReport(valuation?.asset_uuid)
    const scrollContainerRef = useRef<HTMLDivElement>(null)

    const { searchQuery, setSearchQuery, performSearch, searchMatches, activeMatchIndex, resetSearch, navigateToMatch } = usePdfSearch({
        pageRefs,
        setActivePage,
        setJumpPageInput,
    })

    const [isMarking, setIsMarking] = useState(false)
    const [pendingSelection, setPendingSelection] = useState<{
        page: number
        selection?: TextSelection
    } | null>(null)

    const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
        setNumberOfPages(numPages)
        setActivePage(1)
        setJumpPageInput('1')
    }

    useEffect(() => {
        const fetchBlob = async () => {
            if (!valuation?.report_file?.url) {
                return
            }
            const res = await fetch(valuation.report_file.url)
            const blob = await res.blob()
            setDownloadedPdf(blob)
        }

        void fetchBlob()
        // We don't want to use the url as a dependency as that changes frequently
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [valuation?.report_file?.uuid])

    const handleTextSelection = async (page: number, pageRef: HTMLDivElement | null) => {
        if (!isMarking || !pageRef) {
            return
        }
        const selection = window.getSelection()
        if (!selection || selection.isCollapsed) {
            return
        }

        const text = selection.toString()
        const range = selection.getRangeAt(0)
        const rects = Array.from(range.getClientRects())

        if (!pageRef.contains(range.startContainer) || !pageRef.contains(range.endContainer)) {
            selection.removeAllRanges()
            return
        }

        const pdfCanvas = pageRef.querySelector('canvas')
        if (!pdfCanvas) {
            return
        }

        const { left: canvasLeft, top: canvasTop } = pdfCanvas.getBoundingClientRect()

        const scaledRects: Scaled[] = rects.map((r) => {
            const x = (r.left - canvasLeft) / scale
            const y = (r.top - canvasTop) / scale

            return {
                x,
                y,
                width: r.width / scale,
                height: r.height / scale,
                page_number: page,
            }
        })

        selection.removeAllRanges()

        setPendingSelection({
            page,
            selection: { text, rects: scaledRects },
        })
    }

    useEffect(() => {
        const container = scrollContainerRef.current
        if (!container) {
            return
        }

        const handleScroll = () => {
            if (!scrollContainerRef.current) {
                return
            }

            const containerTop = scrollContainerRef.current.getBoundingClientRect().top
            let closestPage = 1
            let closestDistance = Infinity

            pageRefs.current.forEach((ref, index) => {
                if (!ref) {
                    return
                }
                const rect = ref.getBoundingClientRect()
                const distance = Math.abs(rect.top - containerTop)

                if (distance < closestDistance) {
                    closestDistance = distance
                    closestPage = index + 1
                }
            })

            const jump = Math.abs(closestPage - activePage)
            if (jump <= 2) {
                setActivePage(closestPage)
                setJumpPageInput(String(closestPage))
            } else {
                const safePageRef = pageRefs.current[activePage - 1]
                if (safePageRef) {
                    scrollContainerRef.current.scrollTo({ top: safePageRef.offsetTop, behavior: 'instant' })
                }
            }
        }

        container.addEventListener('scroll', handleScroll)
        return () => container.removeEventListener('scroll', handleScroll)
    }, [scale, downloadedPdf, activePage, setActivePage, setJumpPageInput, pageRefs])

    useEffect(() => {
        const getRemainingAnnotationsWidth = () => {
            const container = scrollContainerRef.current
            const pdfPage = pageRefs.current[0]
            if (!container || !pdfPage) {
                return 0
            }
            const containerWidth = container.getBoundingClientRect().width
            const pdfWidth = pdfPage.getBoundingClientRect().width
            return containerWidth - pdfWidth - 104
        }

        const handleResize = () => {
            const remainingWidth = getRemainingAnnotationsWidth()
            setAnnotationPanelWidth(remainingWidth)
        }

        const resizeObserver = new ResizeObserver(handleResize)
        if (scrollContainerRef.current) {
            resizeObserver.observe(scrollContainerRef.current)
        }
        const firstPageRef = pageRefs.current[0]
        if (firstPageRef) {
            resizeObserver.observe(firstPageRef)
        }

        handleResize()

        return () => resizeObserver.disconnect()
    }, [downloadedPdf, numberOfPages, pageRefs])

    const areAnnotationsCollapsed = annotationPanelWidth < 200

    return (
        <Stack direction="column" bgcolor={tokens.ColorBackgroundDark} flex={1} minHeight={0} position="relative">
            {!downloadedPdf || isValuationPlausibilityPending ? (
                <Stack direction="column" height="100%" width="100%" justifyContent="center" alignItems="center" marginTop="-30px">
                    <LottieWrapper style={{ height: '340px' }} animationData={AssembleDocumentLottie} />
                    <Typography variant="textM">{tPlausibilityPdf('loading')}</Typography>
                </Stack>
            ) : (
                <>
                    <Stack
                        direction="row"
                        paddingX={tokens.SizingSpacing500}
                        paddingY={tokens.SizingSpacing150}
                        justifyContent="space-between"
                        width="100%"
                        position="absolute"
                        bottom={0}
                        left={0}
                        zIndex={15}
                    >
                        <Stack direction="row" gap={tokens.SizingSpacing100}>
                            <Box bgcolor={tokens.ColorBackgroundDark} borderRadius={tokens.SizingCornerS} sx={{ ...tokens.Popup }}>
                                <IconButton
                                    variant="secondary"
                                    size="small"
                                    icon={<CaretLeftIcon />}
                                    onClick={() => goToPage(activePage - 1)}
                                    disabled={activePage <= 1}
                                    tooltip={tPlausibilityPdf('actions.previousPage')}
                                />
                            </Box>
                            <InputBase
                                value={jumpPageInput}
                                onChange={(e) => {
                                    setJumpPageInput(e.target.value)
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        const parsed = parseInt(jumpPageInput, 10)
                                        if (!isNaN(parsed)) {
                                            goToPage(parsed)
                                        }
                                    }
                                }}
                                sx={{
                                    width: '60px',
                                    height: '32px',
                                    padding: `0 ${tokens.SizingSpacing150}`,
                                    borderRadius: tokens.SizingCornerS,
                                    border: `${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`,
                                    background: tokens.ColorSurfaceDefault,
                                    '& .MuiInputBase-input': {
                                        textAlign: 'center',
                                        ...tokens.TextM,
                                        color: tokens.ColorTextBold,
                                    },
                                    ...tokens.Popup,
                                }}
                            />
                            <Box bgcolor={tokens.ColorBackgroundDark} borderRadius={tokens.SizingCornerS} sx={{ ...tokens.Popup }}>
                                <IconButton
                                    variant="secondary"
                                    size="small"
                                    icon={<CaretRightIcon />}
                                    onClick={() => goToPage(activePage + 1)}
                                    disabled={activePage >= numberOfPages}
                                    tooltip={tPlausibilityPdf('actions.nextPage')}
                                />
                            </Box>
                        </Stack>
                        <Stack direction="row" gap={tokens.SizingSpacing100}>
                            <Box bgcolor={tokens.ColorBackgroundDark} borderRadius={tokens.SizingCornerS} sx={{ ...tokens.Popup }}>
                                <IconButton
                                    variant="secondary"
                                    size="small"
                                    icon={<MinusIcon />}
                                    onClick={() => setScale((prev) => Math.max(0.5, prev - 0.1))}
                                    tooltip={tPlausibilityPdf('actions.zoomOut')}
                                    disabled={scale === 0.5}
                                />
                            </Box>
                            <Box bgcolor={tokens.ColorBackgroundDark} borderRadius={tokens.SizingCornerS} sx={{ ...tokens.Popup }}>
                                <IconButton
                                    variant="secondary"
                                    size="small"
                                    icon={<PlusIcon />}
                                    onClick={() => setScale((prev) => Math.min(2.0, prev + 0.1))}
                                    tooltip={tPlausibilityPdf('actions.zoomIn')}
                                    disabled={scale === 2.0}
                                />
                            </Box>
                        </Stack>
                    </Stack>
                    <Stack
                        direction="row"
                        paddingX={tokens.SizingSpacing500}
                        paddingY={tokens.SizingSpacing150}
                        justifyContent="space-between"
                        width="100%"
                    >
                        <Box>
                            <InputBase
                                placeholder={tPlausibilityPdf('search.placeholder')}
                                value={searchQuery}
                                onChange={(e) => {
                                    setSearchQuery(e.target.value)
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        performSearch()
                                    }
                                }}
                                endAdornment={
                                    <MagnifyingGlassIcon weight="bold" size={tokens.SizingIconS} color={tokens.ColorIconSubtle} />
                                }
                                sx={{
                                    width: '240px',
                                    height: '32px',
                                    padding: `0 ${tokens.SizingSpacing150}`,
                                    borderRadius: tokens.SizingCornerS,
                                    border: `${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`,
                                    background: tokens.ColorSurfaceDefault,
                                    '& .MuiInputBase-input': {
                                        ...tokens.TextM,
                                        color: tokens.ColorTextBold,
                                    },
                                    '&:focus-within': {
                                        border: `${tokens.SizingBorderS} solid ${tokens.ColorBorderFocused}`,
                                    },
                                    ...tokens.Popup,
                                }}
                            />
                            {searchMatches.length > 0 && (
                                <Stack
                                    position="absolute"
                                    direction="row"
                                    alignItems="center"
                                    borderRadius={tokens.SizingCornerS}
                                    bgcolor={tokens.ColorSurfaceDefault}
                                    padding={tokens.SizingSpacing050}
                                    marginTop={tokens.SizingSpacing100}
                                    sx={{ ...tokens.Popup, zIndex: 50 }}
                                >
                                    <Box sx={{ paddingRight: tokens.SizingSpacing100 }}>
                                        <Typography variant="textS" color={tokens.ColorTextDefault}>
                                            {tPlausibilityPdf('search.results', {
                                                current: activeMatchIndex + 1,
                                                total: searchMatches.length,
                                                resultLabel:
                                                    searchMatches.length === 1
                                                        ? tPlausibilityPdf('search.resultSingle')
                                                        : tPlausibilityPdf('search.resultPlural'),
                                            })}
                                        </Typography>
                                    </Box>

                                    <IconButton
                                        size="small"
                                        variant="tertiary"
                                        icon={<ArrowLeftIcon />}
                                        onClick={() => {
                                            const prevIndex = (activeMatchIndex - 1 + searchMatches.length) % searchMatches.length
                                            navigateToMatch(prevIndex)
                                        }}
                                        tooltip={tPlausibilityPdf('search.previousResult')}
                                    />
                                    <IconButton
                                        size="small"
                                        variant="tertiary"
                                        icon={<ArrowRightIcon />}
                                        onClick={() => {
                                            performSearch()
                                        }}
                                        tooltip={tPlausibilityPdf('search.nextResult')}
                                    />

                                    <IconButton
                                        size="small"
                                        variant="tertiary"
                                        icon={<XIcon />}
                                        onClick={resetSearch}
                                        tooltip={tPlausibilityPdf('search.reset')}
                                    />
                                </Stack>
                            )}
                        </Box>

                        <Stack direction="row" gap={tokens.SizingSpacing300}>
                            <Stack
                                direction="row"
                                gap={tokens.SizingSpacing100}
                                borderRadius={tokens.SizingCornerS}
                                sx={{ ...tokens.Popup }}
                            >
                                <IconButton
                                    variant="secondary"
                                    size="small"
                                    icon={<HighlighterIcon />}
                                    markedGreen={isMarking}
                                    tooltip={tPlausibilityPdf('actions.highlight')}
                                    onClick={() => {
                                        setIsMarking((prev) => !prev)
                                        setPendingSelection(null)
                                    }}
                                    disabled={hasNewerDraft}
                                />
                            </Stack>
                            {isDownloadingReport ? (
                                <div style={{ width: '32px', height: '32px', position: 'relative' }}>
                                    <CircularProgress size={tokens.SizingIconS} sx={{ marginTop: '50%', marginLeft: '50%' }} />
                                </div>
                            ) : (
                                <Box borderRadius={tokens.SizingCornerS} sx={{ ...tokens.Popup }}>
                                    <IconButton
                                        variant="secondary"
                                        size="small"
                                        icon={<DownloadSimpleIcon />}
                                        onClick={() => downloadReport({ valuationId: valuation?.uuid })}
                                        tooltip={tPlausibilityPdf('actions.downloadDraft')}
                                    />
                                </Box>
                            )}
                        </Stack>
                    </Stack>

                    <Stack
                        direction="row"
                        flex={1}
                        minHeight={0}
                        gap={tokens.SizingSpacing100}
                        paddingLeft={tokens.SizingSpacing500}
                        paddingRight={tokens.SizingSpacing200}
                        paddingBottom={tokens.SizingSpacing500}
                        ref={scrollContainerRef}
                        sx={{ overflowY: 'scroll', overflowX: 'auto' }}
                    >
                        <Document options={pdfOptions} file={downloadedPdf} onLoadSuccess={onDocumentLoadSuccess}>
                            <Stack direction="column" gap={tokens.SizingSpacing150}>
                                {range(1, numberOfPages + 1).map((page, index) => {
                                    const annotationsForPage = PdfAnnotations.filter((annotation) => annotation.page_number === page)
                                    const annotationsWithSelection = annotationsForPage.filter((annotation) => annotation.selection)
                                    const IsCreatingAnnotation = pendingSelection?.page === page
                                    const isCollasped = areAnnotationsCollapsed || annotationsForPage.length > 4

                                    return (
                                        <Stack key={page} direction="row" alignItems="flex-start" gap={tokens.SizingSpacing100}>
                                            <div
                                                ref={(el) => {
                                                    pageRefs.current[page - 1] = el
                                                }}
                                                onMouseUp={() => handleTextSelection(page, pageRefs.current[index])}
                                                style={{
                                                    position: 'relative',
                                                    width: 'fit-content',
                                                    border: `${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`,
                                                    boxShadow: tokens.Popup.boxShadow,
                                                }}
                                            >
                                                {annotationsWithSelection.flatMap((pdfAnnotation) =>
                                                    (pdfAnnotation.selection?.rects ?? []).map((rect) => (
                                                        <div
                                                            key={`${pdfAnnotation.uuid}-${rect.x}-${rect.y}`}
                                                            onMouseEnter={() => setHoveredAnnotationId(pdfAnnotation.uuid)}
                                                            onMouseLeave={() => setHoveredAnnotationId(undefined)}
                                                            style={{
                                                                position: 'absolute',
                                                                left: `${rect.x * scale}px`,
                                                                top: `${rect.y * scale}px`,
                                                                width: `${rect.width * scale}px`,
                                                                height: `${rect.height * scale}px`,
                                                                backgroundColor:
                                                                    hoveredAnnotationId === pdfAnnotation.uuid
                                                                        ? 'rgba(255, 238, 0, 1)'
                                                                        : 'rgba(255, 238, 0, 0.5)',
                                                                pointerEvents: 'auto',
                                                                zIndex: 10,
                                                                mixBlendMode: 'multiply',
                                                            }}
                                                        />
                                                    ))
                                                )}
                                                {pendingSelection?.page === page &&
                                                    (pendingSelection.selection?.rects ?? []).map((rect) => (
                                                        <div
                                                            key={`pending-${rect.x}-${rect.y}`}
                                                            style={{
                                                                position: 'absolute',
                                                                left: `${rect.x * scale}px`,
                                                                top: `${rect.y * scale}px`,
                                                                width: `${rect.width * scale}px`,
                                                                height: `${rect.height * scale}px`,
                                                                backgroundColor: 'rgba(255, 220, 0, 1)',
                                                                pointerEvents: 'none',
                                                                zIndex: 9,
                                                                mixBlendMode: 'multiply',
                                                            }}
                                                        />
                                                    ))}
                                                <Page
                                                    pageNumber={page}
                                                    scale={scale}
                                                    width={500}
                                                    renderTextLayer
                                                    renderAnnotationLayer={false}
                                                />
                                            </div>

                                            <Stack
                                                direction="column"
                                                gap={tokens.SizingSpacing100}
                                                width={`max(${annotationPanelWidth}px, 32px)`}
                                            >
                                                <Stack
                                                    direction="column"
                                                    position="relative"
                                                    gap={tokens.SizingSpacing100}
                                                    width={isCollasped ? '32px' : undefined}
                                                >
                                                    <Box borderRadius={tokens.SizingCornerS} sx={{ ...tokens.Popup, width: 'fit-content' }}>
                                                        <IconButton
                                                            variant="secondary"
                                                            size="small"
                                                            markedYellow
                                                            tooltip={tPlausibilityPdf('actions.newPageAnnotation')}
                                                            icon={<PlusCircleIcon />}
                                                            onClick={() => {
                                                                setPendingSelection({ page })
                                                                setIsMarking(false)
                                                            }}
                                                            disabled={hasNewerDraft}
                                                        />
                                                    </Box>
                                                    {IsCreatingAnnotation ? (
                                                        isCollasped ? (
                                                            <ClickAwayListener onClickAway={() => setPendingSelection(null)}>
                                                                <Box position="absolute" zIndex={15} right={40} top={0} width="360px">
                                                                    <TextBubbleCreation
                                                                        onCancel={() => setPendingSelection(null)}
                                                                        type="popup"
                                                                        currentUser={currentUser}
                                                                        onSubmit={(text) => {
                                                                            if (!pendingSelection) {
                                                                                return
                                                                            }

                                                                            addAnnotation(
                                                                                {
                                                                                    text,
                                                                                    page_number: pendingSelection.page,
                                                                                    selection: pendingSelection.selection,
                                                                                    annotation_type: 'pdf',
                                                                                },
                                                                                () => setPendingSelection(null)
                                                                            )
                                                                        }}
                                                                        isPending={isAddAnnotationPending}
                                                                        isImmediatelyVisible={isImmediatelyVisible}
                                                                        copy={textBubbleCopy}
                                                                        userLinkCopy={userLinkCopy}
                                                                    />
                                                                </Box>
                                                            </ClickAwayListener>
                                                        ) : (
                                                            <TextBubbleCreation
                                                                onCancel={() => setPendingSelection(null)}
                                                                type="popup"
                                                                currentUser={currentUser}
                                                                onSubmit={(text) => {
                                                                    if (!pendingSelection) {
                                                                        return
                                                                    }

                                                                    addAnnotation(
                                                                        {
                                                                            text,
                                                                            page_number: pendingSelection.page,
                                                                            selection: pendingSelection.selection,
                                                                            annotation_type: 'pdf',
                                                                        },
                                                                        () => setPendingSelection(null)
                                                                    )
                                                                }}
                                                                isPending={isAddAnnotationPending}
                                                                isImmediatelyVisible={isImmediatelyVisible}
                                                                copy={textBubbleCopy}
                                                                userLinkCopy={userLinkCopy}
                                                            />
                                                        )
                                                    ) : null}
                                                </Stack>
                                                {annotationsForPage.map((annotation) => {
                                                    const isHovered = hoveredAnnotationId === annotation.uuid
                                                    const isSelected = selectedAnnotationId === annotation.uuid
                                                    const noOtherAnnotationsSelected = !selectedAnnotationId
                                                    const showPopupWhenCollapsed = (noOtherAnnotationsSelected && isHovered) || isSelected
                                                    return (
                                                        <ClickAwayListener
                                                            key={annotation.uuid}
                                                            onClickAway={() => isSelected && setSelectedAnnotationId(undefined)}
                                                        >
                                                            <Box position="relative" width={isCollasped ? '32px' : undefined}>
                                                                <TextBubble
                                                                    type="popup"
                                                                    annotation={annotation}
                                                                    addReply={addReply}
                                                                    editAnnotation={editAnnotation}
                                                                    editReply={editReply}
                                                                    deleteAnnotation={deleteAnnotation}
                                                                    deleteReply={deleteReply}
                                                                    isHovered={isHovered}
                                                                    isCollapsed={isCollasped}
                                                                    onMouseEnter={() => setHoveredAnnotationId(annotation.uuid)}
                                                                    onMouseLeave={() => setHoveredAnnotationId(undefined)}
                                                                    onClick={() => {
                                                                        setSelectedAnnotationId(annotation.uuid)
                                                                    }}
                                                                    currentUser={currentUser}
                                                                    isAddAnnotationPending={isAddAnnotationPending}
                                                                    isDeleteAnnotationPending={isDeleteAnnotationPending}
                                                                    isEditAnnotationPending={isEditAnnotationPending}
                                                                    isImmediatelyVisible={isImmediatelyVisible}
                                                                    showDraftVersion={hasMultipleDrafts}
                                                                    draftId={valuation?.uuid}
                                                                    hasNewerDraft={hasNewerDraft}
                                                                    copy={textBubbleCopy}
                                                                    userLinkCopy={userLinkCopy}
                                                                    locale={locale}
                                                                />
                                                                {showPopupWhenCollapsed && isCollasped && !IsCreatingAnnotation && (
                                                                    <Box position="absolute" zIndex={15} right={40} top={0} width="360px">
                                                                        <TextBubble
                                                                            type="popup"
                                                                            key={annotation.uuid}
                                                                            annotation={annotation}
                                                                            addReply={addReply}
                                                                            editAnnotation={editAnnotation}
                                                                            editReply={editReply}
                                                                            deleteAnnotation={deleteAnnotation}
                                                                            deleteReply={deleteReply}
                                                                            currentUser={currentUser}
                                                                            isAddAnnotationPending={isAddAnnotationPending}
                                                                            isDeleteAnnotationPending={isDeleteAnnotationPending}
                                                                            isEditAnnotationPending={isEditAnnotationPending}
                                                                            isImmediatelyVisible={isImmediatelyVisible}
                                                                            showDraftVersion={hasMultipleDrafts}
                                                                            draftId={valuation?.uuid}
                                                                            hasNewerDraft={hasNewerDraft}
                                                                            copy={textBubbleCopy}
                                                                            userLinkCopy={userLinkCopy}
                                                                            locale={locale}
                                                                        />
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                        </ClickAwayListener>
                                                    )
                                                })}
                                            </Stack>
                                        </Stack>
                                    )
                                })}
                            </Stack>
                        </Document>
                    </Stack>
                </>
            )}
        </Stack>
    )
}
